import Button from '@/components/Buttons/Button';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import { toast } from '@/components/toast';
import { fetchOwnerVoices, updateVoice } from '@/services/api/did-api/backoffice-api';
import { IVoice } from '@d-id/types';
import { Box, CircularProgress, Paper, styled, TextField, Typography } from '@mui/material';
import { useState } from 'react';

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    fontSize: '1.5rem',
}));

const VoiceList = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const VoiceItem = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

export const VoiceManagementForm = () => {
    const [ownerId, setOwnerId] = useState('');
    const [voices, setVoices] = useState<IVoice[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState('');
    const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
    const [editingVoice, setEditingVoice] = useState<string | null>(null);
    const [newVoiceName, setNewVoiceName] = useState('');
    const [newOwnerId, setNewOwnerId] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);

    const handleFetchVoices = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setVoices([]);
        setHasAttemptedFetch(true);

        try {
            if (!ownerId) {
                setError('Please enter a valid owner ID');
                return;
            }

            const fetchedVoices = await fetchOwnerVoices(ownerId);
            setVoices(fetchedVoices);
        } catch (error) {
            setError('Failed to fetch voices. Please check the owner ID and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateVoice = async (voiceId: string) => {
        if (newOwnerId && newOwnerId !== ownerId) {
            setShowConfirmation(true);
            return;
        }

        await performVoiceUpdate(voiceId);
    };

    const performVoiceUpdate = async (voiceId: string) => {
        setIsUpdating(true);
        try {
            const payload: { name?: string; overrideOwnerId?: string; newOwnerId?: string } = {
                overrideOwnerId: ownerId,
            };

            if (newVoiceName) {
                payload.name = newVoiceName;
            }
            if (newOwnerId) {
                payload.newOwnerId = newOwnerId;
            }

            await updateVoice(voiceId, payload);
            toast.success({ title: 'Voice updated successfully' });

            // Refresh the voices list
            const updatedVoices = await fetchOwnerVoices(ownerId);
            setVoices(updatedVoices);

            // Reset editing state
            setEditingVoice(null);
            setNewVoiceName('');
            setNewOwnerId('');
        } catch (error) {
            toast.error({ title: 'Failed to update voice' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleConfirmUpdate = async () => {
        if (editingVoice) {
            await performVoiceUpdate(editingVoice);
            setShowConfirmation(false);
        }
    };

    const handleEditClick = (voice: IVoice) => {
        setEditingVoice(voice.id);
        setNewVoiceName(voice.name);
    };

    return (
        <Box>
            <StyledForm onSubmit={handleFetchVoices}>
                <TextField label="Owner ID" value={ownerId} onChange={e => setOwnerId(e.target.value)} fullWidth required />
                <StyledButton type="submit" disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} /> : 'Fetch Voices'}
                </StyledButton>
            </StyledForm>

            {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}

            {!isLoading && hasAttemptedFetch && voices.length === 0 && (
                <Typography sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>No voices found for this owner ID</Typography>
            )}

            <VoiceList>
                {voices.map(voice => (
                    <VoiceItem key={voice.id}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">{voice.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Voice ID: {voice.id}
                            </Typography>
                        </Box>
                        {editingVoice === voice.id ? (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="New Name"
                                    value={newVoiceName}
                                    onChange={e => setNewVoiceName(e.target.value)}
                                    size="small"
                                />
                                <TextField
                                    label="New Owner ID"
                                    value={newOwnerId}
                                    onChange={e => setNewOwnerId(e.target.value)}
                                    size="small"
                                />
                                <Button onClick={() => handleUpdateVoice(voice.id)} disabled={isUpdating}>
                                    {isUpdating ? <CircularProgress size={24} /> : 'Save'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setEditingVoice(null);
                                        setNewVoiceName('');
                                        setNewOwnerId('');
                                    }}
                                    disabled={isUpdating}>
                                    Cancel
                                </Button>
                            </Box>
                        ) : (
                            <Button onClick={() => handleEditClick(voice)}>Edit</Button>
                        )}
                    </VoiceItem>
                ))}
            </VoiceList>

            <ConfirmationModal
                open={showConfirmation}
                onClose={() => {
                    setShowConfirmation(false);
                }}
                title="Change Voice Owner"
                text="Are you sure you want to change the voice owner? This will remove the voice from the current owner."
                primaryButtonText="Yes, change owner"
                secondaryButtonText="Cancel"
                onConfirm={handleConfirmUpdate}
                onSecondaryClick={() => {
                    setShowConfirmation(false);
                }}
                isLoading={isUpdating}
            />
        </Box>
    );
};
